import type { Tool } from '@xsai/shared-chat'

import { tool } from '@xsai/tool'
import { z } from 'zod'

// Supported TCG slugs (must match server-side)
const tcgSlugs = [
  'pokemon',
  'one-piece',
  'dragon-ball-fusion',
  'digimon',
  'magic',
  'union-arena',
  'gundam',
  'star-wars-unlimited',
  'riftbound',
] as const

type Tcg = typeof tcgSlugs[number]

// NOTICE: Rarity hierarchies for different TCGs (highest rarity first)
// These are used for fallback logic when a specific rarity isn't found
const RARITY_HIERARCHIES: Record<Tcg, string[]> = {
  'one-piece': [
    'Secret Rare', 'SEC', 'Special Art', 'SP',
    'Super Rare', 'SR', 'Manga Rare', 'Treasure Rare',
    'Leader', 'L',
    'Rare', 'R',
    'Uncommon', 'UC',
    'Common', 'C',
  ],
  'pokemon': [
    'Secret Rare', 'Hyper Rare', 'Special Art Rare', 'SAR',
    'Ultra Rare', 'Art Rare', 'AR',
    'Double Rare', 'RR',
    'Rare Holo', 'Holo Rare',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
  'digimon': [
    'Secret Rare', 'SEC',
    'Super Rare', 'SR',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
  'magic': [
    'Mythic Rare', 'Mythic', 'M',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
  'dragon-ball-fusion': [
    'Secret Rare', 'SCR',
    'Special Rare', 'SPR',
    'Super Rare', 'SR',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
  'union-arena': [
    'Secret Rare', 'SEC',
    'Super Rare', 'SR',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
  'gundam': [
    'Secret Rare', 'SEC',
    'Super Rare', 'SR',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
  'star-wars-unlimited': [
    'Legendary', 'L',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
  'riftbound': [
    'Mythic', 'M',
    'Rare', 'R',
    'Uncommon', 'U',
    'Common', 'C',
  ],
}

// NOTICE: Rarity tiers for "good" cards (used for favorite picking)
// Cards at or above these rarities are considered desirable
const HIGH_VALUE_RARITIES: Record<Tcg, string[]> = {
  'one-piece': ['Secret Rare', 'SEC', 'Special Art', 'SP', 'Super Rare', 'SR', 'Manga Rare', 'Treasure Rare', 'Leader', 'L'],
  'pokemon': ['Secret Rare', 'Hyper Rare', 'Special Art Rare', 'SAR', 'Ultra Rare', 'Art Rare', 'AR', 'Double Rare', 'RR'],
  'digimon': ['Secret Rare', 'SEC', 'Super Rare', 'SR'],
  'magic': ['Mythic Rare', 'Mythic', 'M', 'Rare', 'R'],
  'dragon-ball-fusion': ['Secret Rare', 'SCR', 'Special Rare', 'SPR', 'Super Rare', 'SR'],
  'union-arena': ['Secret Rare', 'SEC', 'Super Rare', 'SR'],
  'gundam': ['Secret Rare', 'SEC', 'Super Rare', 'SR'],
  'star-wars-unlimited': ['Legendary', 'L', 'Rare', 'R'],
  'riftbound': ['Mythic', 'M', 'Rare', 'R'],
}

let proxyBaseUrl: string | null = null

/**
 * Set the proxy server base URL at runtime
 */
export function setApitcgProxyUrl(url: string) {
  proxyBaseUrl = url.replace(/\/$/, '') // Remove trailing slash
}

/**
 * Get the current proxy URL
 */
export function getApitcgProxyUrl(): string | null {
  return proxyBaseUrl
}

/**
 * Check if APITCG tools are enabled (proxy URL is set)
 */
export function isApitcgEnabled(): boolean {
  return !!proxyBaseUrl
}

async function proxyPost(endpoint: string, body: Record<string, unknown>) {
  if (!proxyBaseUrl) {
    throw new Error('APITCG proxy URL is not configured')
  }

  const res = await fetch(`${proxyBaseUrl}/api/proxy/tcg${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(error.error || `Request failed: ${res.status}`)
  }

  return res.json()
}

async function searchCards(args: { tcg: Tcg, property: string, value: string, limit?: number }) {
  return proxyPost('/search', args)
}

async function getCard(args: { tcg: Tcg, id: string }) {
  return proxyPost('/card', args)
}

/**
 * Format card data for display
 */
function formatCardResult(card: Record<string, unknown>): string {
  const parts: string[] = []

  // Card ID is the most important identifier (e.g. OP13-118)
  const cardId = card.code || card.id || card.cardId || card.number
  if (card.name && cardId)
    parts.push(`**${card.name}** (${cardId})`)
  else if (card.name)
    parts.push(`**${card.name}**`)
  else if (cardId)
    parts.push(`Card ID: ${cardId}`)

  if (card.rarity)
    parts.push(`Rarity: ${card.rarity}`)
  if (card.type)
    parts.push(`Type: ${card.type}`)
  if (card.color)
    parts.push(`Color: ${card.color}`)
  if (card.cost !== undefined)
    parts.push(`Cost: ${card.cost}`)
  if (card.power !== undefined)
    parts.push(`Power: ${card.power}`)
  if (card.counter !== undefined)
    parts.push(`Counter: ${card.counter}`)
  if (card.attribute)
    parts.push(`Attribute: ${card.attribute}`)
  if (card.effect)
    parts.push(`Effect: ${card.effect}`)
  if (card.text)
    parts.push(`Text: ${card.text}`)
  if (card.image || card.imageUrl)
    parts.push(`Image: ${card.image || card.imageUrl}`)
  // Include price if available
  if (card.price !== undefined)
    parts.push(`Price: $${card.price}`)
  if (card.marketPrice !== undefined)
    parts.push(`Market Price: $${card.marketPrice}`)

  return parts.join('\n')
}

/**
 * Get the rarity tier index (lower = rarer)
 */
function getRarityTier(tcg: Tcg, rarity: string): number {
  const hierarchy = RARITY_HIERARCHIES[tcg] || []
  const normalizedRarity = rarity.toLowerCase().trim()
  const index = hierarchy.findIndex(r => r.toLowerCase() === normalizedRarity)
  return index === -1 ? 999 : index
}

/**
 * Sort cards by rarity (rarest first)
 */
function sortByRarity(tcg: Tcg, cards: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...cards].sort((a, b) => {
    const rarityA = String(a.rarity || '')
    const rarityB = String(b.rarity || '')
    return getRarityTier(tcg, rarityA) - getRarityTier(tcg, rarityB)
  })
}

/**
 * Filter to high-value cards only
 */
function filterHighValueCards(tcg: Tcg, cards: Record<string, unknown>[]): Record<string, unknown>[] {
  const highValueRarities = HIGH_VALUE_RARITIES[tcg] || []
  return cards.filter((card) => {
    const rarity = String(card.rarity || '').toLowerCase().trim()
    return highValueRarities.some(r => r.toLowerCase() === rarity)
  })
}

const tcgEnum = z.enum(tcgSlugs)

const searchCardsSchema = z.object({
  tcg: tcgEnum.describe('TCG slug (e.g. pokemon, one-piece, digimon, magic)'),
  property: z.string().describe('Query property (name, code, id, rarity, type, etc.)'),
  value: z.string().describe('Search value'),
})

const getCardSchema = z.object({
  tcg: tcgEnum.describe('TCG slug (e.g. pokemon, one-piece, digimon, magic)'),
  id: z.string().describe('Exact card id/code (e.g. OP06-014, base1-4, ST01-001)'),
})

const findRarestCardsSchema = z.object({
  tcg: tcgEnum.describe('TCG slug (e.g. pokemon, one-piece, digimon, magic)'),
  targetRarity: z.string().describe('Target rarity to start with (e.g. "Secret Rare", "SR"). Use empty string "" to start from highest. If not found, will fall back to next available rarity tier.'),
  name: z.string().describe('Filter by card name. Use empty string "" for no filter.'),
  limit: z.number().describe('Max cards to return. Use 5 as default.'),
})

const findFavoriteCardSchema = z.object({
  tcg: tcgEnum.describe('TCG slug (e.g. pokemon, one-piece, digimon, magic)'),
  name: z.string().describe('Filter by character/card name to pick favorite from. Use empty string "" for any card.'),
})

const findCardsByPriceSchema = z.object({
  tcg: tcgEnum.describe('TCG slug (e.g. pokemon, one-piece, digimon, magic)'),
  order: z.enum(['cheapest', 'expensive']).describe('Sort order: "cheapest" for lowest price first, "expensive" for highest price first'),
  name: z.string().describe('Filter by card name. Use empty string "" for no filter.'),
  rarity: z.string().describe('Filter by specific rarity. Use empty string "" for no filter.'),
  limit: z.number().describe('Max cards to return. Use 5 as default.'),
})

const tools: Promise<Tool>[] = [
  // Basic search tool
  tool({
    name: 'tcg_search_cards',
    description: `Search for trading card game cards by property. Supports: ${tcgSlugs.join(', ')}. Common properties: name (partial match), code/id (exact), rarity, type, color. Returns up to 10 results with card details.`,
    execute: async (params) => {
      if (!isApitcgEnabled()) {
        return 'TCG service not available. Server proxy not configured.'
      }

      try {
        const result = await searchCards(params as Parameters<typeof searchCards>[0])
        const cards = result?.cards ?? []

        if (cards.length === 0) {
          return `No cards found for ${params.property}="${params.value}" in ${params.tcg}`
        }

        const formatted = cards.slice(0, 10).map((card: Record<string, unknown>) => formatCardResult(card)).join('\n\n---\n\n')
        return `Found ${cards.length} card(s):\n\n${formatted}`
      }
      catch (error) {
        return `Error searching cards: ${(error as Error).message}`
      }
    },
    parameters: searchCardsSchema,
  }),

  // Get specific card by ID - ALWAYS use this for exact card ID lookups like OP10-030
  tool({
    name: 'tcg_get_card',
    description: `Get detailed information about a specific trading card by its EXACT ID/code. ALWAYS use this tool when user mentions a specific card ID like OP10-030, OP06-014, ST01-001, BT1-001. This does a direct lookup for that exact card. Supports: ${tcgSlugs.join(', ')}.`,
    execute: async (params) => {
      if (!isApitcgEnabled()) {
        return 'TCG service not available. Server proxy not configured.'
      }

      const tcg = params.tcg as Tcg
      const cardId = params.id as string

      try {
        // First try direct lookup
        const result = await getCard({ tcg, id: cardId })
        const card = result?.card

        if (card && typeof card === 'object' && Object.keys(card).length > 0) {
          return formatCardResult(card)
        }

        // If direct lookup fails, try searching by code/id property
        const searchResult = await searchCards({ tcg, property: 'code', value: cardId, limit: 5 })
        const cards = searchResult?.cards ?? []

        // Find exact match in search results
        const exactMatch = cards.find((c: Record<string, unknown>) => {
          const code = String(c.code || c.id || c.cardId || c.number || '').toUpperCase()
          return code === cardId.toUpperCase()
        })

        if (exactMatch) {
          return formatCardResult(exactMatch)
        }

        // Try searching by id property as fallback
        const searchById = await searchCards({ tcg, property: 'id', value: cardId, limit: 5 })
        const cardsById = searchById?.cards ?? []

        const exactMatchById = cardsById.find((c: Record<string, unknown>) => {
          const code = String(c.code || c.id || c.cardId || c.number || '').toUpperCase()
          return code === cardId.toUpperCase()
        })

        if (exactMatchById) {
          return formatCardResult(exactMatchById)
        }

        return `Card not found: ${cardId} in ${tcg}. Make sure the card ID is correct (e.g. OP10-030, ST01-001).`
      }
      catch (error) {
        return `Error fetching card ${cardId}: ${(error as Error).message}`
      }
    },
    parameters: getCardSchema,
  }),

  // Find rarest cards with fallback
  tool({
    name: 'tcg_find_rarest_cards',
    description: `Find the rarest cards in a TCG with automatic fallback. If the target rarity (e.g. "Secret Rare") has no cards, automatically tries the next rarity tier down (e.g. "Super Rare" -> "Rare"). Perfect for answering "what are the rarest X cards?" questions. Supports: ${tcgSlugs.join(', ')}.`,
    execute: async (params) => {
      if (!isApitcgEnabled()) {
        return 'TCG service not available. Server proxy not configured.'
      }

      const tcg = params.tcg as Tcg
      const limit = (params.limit as number) || 5
      const hierarchy = RARITY_HIERARCHIES[tcg] || []
      // Treat empty string as "no filter"
      const targetRarity = (params.targetRarity as string) || ''
      const nameFilter = (params.name as string) || ''

      // Determine starting point in rarity hierarchy
      let startIndex = 0
      if (targetRarity) {
        const idx = hierarchy.findIndex(r => r.toLowerCase() === targetRarity.toLowerCase())
        if (idx !== -1)
          startIndex = idx
      }

      // Try each rarity from highest to lowest until we find cards
      const triedRarities: string[] = []
      for (let i = startIndex; i < hierarchy.length; i++) {
        const rarity = hierarchy[i]
        // Skip short codes if we already tried the full name
        if (rarity.length <= 3 && triedRarities.some(t => t.toLowerCase().includes(rarity.toLowerCase()))) {
          continue
        }
        triedRarities.push(rarity)

        try {
          // Search by rarity
          let result = await searchCards({ tcg, property: 'rarity', value: rarity, limit: 50 })
          let cards = result?.cards ?? []

          // If name filter provided, also filter by name
          if (nameFilter && cards.length > 0) {
            const nameLower = nameFilter.toLowerCase()
            cards = cards.filter((c: Record<string, unknown>) =>
              String(c.name || '').toLowerCase().includes(nameLower),
            )
          }

          if (cards.length > 0) {
            const sorted = sortByRarity(tcg, cards)
            const selected = sorted.slice(0, limit)
            const formatted = selected.map((card: Record<string, unknown>) => formatCardResult(card)).join('\n\n---\n\n')

            let response = `Found ${cards.length} ${rarity} card(s)`
            if (targetRarity && rarity.toLowerCase() !== targetRarity.toLowerCase()) {
              response += ` (Note: "${targetRarity}" cards not found, showing "${rarity}" instead)`
            }
            if (nameFilter) {
              response += ` matching "${nameFilter}"`
            }
            response += `:\n\n${formatted}`

            return response
          }
        }
        catch {
          // Continue to next rarity on error
        }
      }

      return `No cards found in ${tcg}. Tried rarities: ${triedRarities.join(', ')}`
    },
    parameters: findRarestCardsSchema,
  }),

  // Pick a favorite card
  tool({
    name: 'tcg_pick_favorite_card',
    description: `Pick a random "favorite" card from high-value rarities (Secret Rare, Super Rare, etc.). Use this when user asks for your favorite card, a card recommendation, or wants you to pick a cool card. Returns one random rare/valuable card. Supports: ${tcgSlugs.join(', ')}.`,
    execute: async (params) => {
      if (!isApitcgEnabled()) {
        return 'TCG service not available. Server proxy not configured.'
      }

      const tcg = params.tcg as Tcg
      // Treat empty string as "no filter"
      const nameFilter = (params.name as string) || ''
      const highValueRarities = HIGH_VALUE_RARITIES[tcg] || []

      // Try high-value rarities until we find cards
      const allCards: Record<string, unknown>[] = []

      for (const rarity of highValueRarities.slice(0, 4)) { // Check top 4 rarities
        try {
          const result = await searchCards({ tcg, property: 'rarity', value: rarity, limit: 30 })
          let cards = result?.cards ?? []

          if (nameFilter && cards.length > 0) {
            const nameLower = nameFilter.toLowerCase()
            cards = cards.filter((c: Record<string, unknown>) =>
              String(c.name || '').toLowerCase().includes(nameLower),
            )
          }

          allCards.push(...cards)

          // Stop if we have enough cards to pick from
          if (allCards.length >= 20)
            break
        }
        catch {
          // Continue on error
        }
      }

      if (allCards.length === 0) {
        return nameFilter
          ? `Couldn't find any high-value cards matching "${nameFilter}" in ${tcg}.`
          : `Couldn't find any high-value cards in ${tcg}.`
      }

      // Pick a random card
      const randomIndex = Math.floor(Math.random() * allCards.length)
      const favorite = allCards[randomIndex]

      const intro = nameFilter
        ? `My favorite ${nameFilter} card is:\n\n`
        : `Here's a card I really like:\n\n`

      return intro + formatCardResult(favorite)
    },
    parameters: findFavoriteCardSchema,
  }),

  // Find cards by price (with limitations note)
  tool({
    name: 'tcg_find_cards_by_value',
    description: `Find cheapest or most expensive cards based on rarity (as a proxy for value). Since direct price data isn't always available, this uses rarity tier as value indicator: common = lowest value, secret rare = highest value. Use "cheapest" to find common/uncommon cards, "expensive" to find the rarest cards. Supports: ${tcgSlugs.join(', ')}.`,
    execute: async (params) => {
      if (!isApitcgEnabled()) {
        return 'TCG service not available. Server proxy not configured.'
      }

      const tcg = params.tcg as Tcg
      const order = params.order as 'cheapest' | 'expensive'
      const limit = (params.limit as number) || 5
      // Treat empty string as "no filter"
      const nameFilter = (params.name as string) || ''
      const rarityFilter = (params.rarity as string) || ''
      const hierarchy = RARITY_HIERARCHIES[tcg] || []

      // For cheapest: start from bottom of hierarchy (Common)
      // For expensive: start from top (Secret Rare)
      const searchOrder = order === 'cheapest'
        ? [...hierarchy].reverse()
        : hierarchy

      const allCards: Record<string, unknown>[] = []

      for (const rarity of searchOrder) {
        // Skip if specific rarity requested and doesn't match
        if (rarityFilter && rarity.toLowerCase() !== rarityFilter.toLowerCase()) {
          continue
        }

        // Skip short codes to avoid duplicates
        if (rarity.length <= 3)
          continue

        try {
          const result = await searchCards({ tcg, property: 'rarity', value: rarity, limit: 30 })
          let cards = result?.cards ?? []

          if (nameFilter && cards.length > 0) {
            const nameLower = nameFilter.toLowerCase()
            cards = cards.filter((c: Record<string, unknown>) =>
              String(c.name || '').toLowerCase().includes(nameLower),
            )
          }

          allCards.push(...cards)

          // For cheapest/expensive, just need cards from the target tier
          if (allCards.length >= limit)
            break
        }
        catch {
          // Continue on error
        }
      }

      if (allCards.length === 0) {
        return `No cards found in ${tcg}${nameFilter ? ` matching "${nameFilter}"` : ''}`
      }

      // Sort by rarity tier
      const sorted = order === 'cheapest'
        ? sortByRarity(tcg, allCards).reverse()
        : sortByRarity(tcg, allCards)

      const selected = sorted.slice(0, limit)
      const formatted = selected.map((card: Record<string, unknown>) => formatCardResult(card)).join('\n\n---\n\n')

      const orderDesc = order === 'cheapest' ? 'lowest value (common)' : 'highest value (rarest)'
      let response = `Found ${selected.length} ${orderDesc} cards`
      if (nameFilter)
        response += ` matching "${nameFilter}"`
      response += `:\n\n${formatted}`
      response += `\n\n(Note: Values based on rarity tier. For actual market prices, check TCGPlayer or CardMarket.)`

      return response
    },
    parameters: findCardsByPriceSchema,
  }),
]

export async function apitcg(): Promise<Tool[]> {
  // Only return tools if proxy URL is configured
  if (!isApitcgEnabled()) {
    return []
  }
  return Promise.all(tools)
}
